// Deterministic statistical lottery engine (pure JS, no external API).

// ---- Random helpers ----
function weightedSampleWithout(pool, weights, k, forced = [], excluded = []) {
  const chosen = new Set(forced.filter((n) => pool.includes(n)));
  const ex = new Set(excluded);
  let candidates = pool.filter((n) => !chosen.has(n) && !ex.has(n));
  let w = candidates.map((n) => weights[n] || 1);
  while (chosen.size < k && candidates.length > 0) {
    const total = w.reduce((a, b) => a + b, 0);
    let r = Math.random() * total;
    let idx = 0;
    for (; idx < w.length; idx++) {
      r -= w[idx];
      if (r <= 0) break;
    }
    if (idx >= candidates.length) idx = candidates.length - 1;
    chosen.add(candidates[idx]);
    candidates.splice(idx, 1);
    w.splice(idx, 1);
  }
  return Array.from(chosen).sort((a, b) => a - b);
}

// ---- Stats from history ----
export function computeGaps(config, draws) {
  // gap = number of draws since last appearance (0 = appeared in latest draw)
  const gaps = {};
  for (let n = 1; n <= config.max; n++) {
    let g = draws.length;
    for (let i = 0; i < draws.length; i++) {
      if (draws[i].numbers.includes(n)) {
        g = i;
        break;
      }
    }
    gaps[n] = g;
  }
  return gaps;
}

export function computeFrequencies(config, draws) {
  const freq = {};
  for (let n = 1; n <= config.max; n++) freq[n] = 0;
  draws.forEach((d) => d.numbers.forEach((n) => { if (freq[n] !== undefined) freq[n] += 1; }));
  return freq;
}

// ---- Validators ----
export function validateSum(nums, config) {
  const s = nums.reduce((a, b) => a + b, 0);
  if (config.kenoMode) {
    const mean = s / nums.length;
    return mean >= 30 && mean <= 41;
  }
  return s >= config.sumMin && s <= config.sumMax;
}

export function validateParity(nums, config) {
  const evens = nums.filter((n) => n % 2 === 0).length;
  if (config.kenoMode) {
    return Math.abs(evens - (nums.length - evens)) <= 2;
  }
  return config.parityEvens.includes(evens);
}

export function countConsecutivePairs(nums) {
  const s = [...nums].sort((a, b) => a - b);
  let c = 0;
  for (let i = 0; i < s.length - 1; i++) if (s[i + 1] - s[i] === 1) c++;
  return c;
}

export function validateConsecutives(nums) {
  return countConsecutivePairs(nums) <= 1;
}

export function validateMemory(nums, lastDraw) {
  if (!lastDraw) return true;
  const common = nums.filter((n) => lastDraw.numbers.includes(n)).length;
  return common <= 1;
}

export function decadeCount(nums) {
  const set = new Set(nums.map((n) => Math.floor((n - 1) / 10)));
  return set.size;
}

export function validateDecades(nums) {
  return decadeCount(nums) >= 3;
}

export function highNumbersCount(nums) {
  return nums.filter((n) => n > 31).length;
}

export function validateAntiShare(nums) {
  return highNumbersCount(nums) >= 3;
}

// ---- Conformity score 0..100 ----
export function calculateConformityScore(nums, config, draws) {
  let score = 0;
  const s = nums.reduce((a, b) => a + b, 0);

  // Sum (30 pts) — closeness to center of ideal window
  if (config.kenoMode) {
    const mean = s / nums.length;
    score += mean >= 30 && mean <= 41 ? 30 : Math.max(0, 30 - Math.abs(35.5 - mean) * 3);
  } else {
    const mid = (config.sumMin + config.sumMax) / 2;
    const half = (config.sumMax - config.sumMin) / 2;
    const dist = Math.min(1, Math.abs(s - mid) / half);
    score += validateSum(nums, config) ? 30 - dist * 8 : Math.max(0, 15 - dist * 15);
  }

  // Parity (20 pts)
  score += validateParity(nums, config) ? 20 : 4;

  // Decades (18 pts)
  const dc = decadeCount(nums);
  score += Math.min(18, dc * 6);

  // Consecutives (16 pts)
  const cp = countConsecutivePairs(nums);
  score += cp <= 1 ? 16 : Math.max(0, 16 - cp * 8);

  // Memory (16 pts)
  score += validateMemory(nums, draws && draws[0]) ? 16 : 4;

  return Math.max(0, Math.min(100, Math.round(score)));
}

// ---- Full diagnostic for analyzer ----
export function diagnoseGrid(nums, config, draws) {
  const s = nums.reduce((a, b) => a + b, 0);
  const evens = nums.filter((n) => n % 2 === 0).length;
  const odds = nums.length - evens;
  const cp = countConsecutivePairs(nums);
  const dc = decadeCount(nums);
  const high = highNumbersCount(nums);
  const last = draws && draws[0];
  const common = last ? nums.filter((n) => last.numbers.includes(n)).length : 0;

  const rules = [];
  if (config.kenoMode) {
    const mean = (s / nums.length).toFixed(1);
    rules.push({
      key: "sum", label: "Somme / moyenne", value: `Σ=${s} · moy=${mean}`,
      status: validateSum(nums, config) ? "pass" : "fail",
      detail: "Moyenne idéale entre 30 et 41 par numéro",
    });
  } else {
    rules.push({
      key: "sum", label: "Somme des numéros", value: `Σ = ${s}`,
      status: validateSum(nums, config) ? "pass" : (Math.abs(s - (config.sumMin + config.sumMax) / 2) < (config.sumMax - config.sumMin) ? "warn" : "fail"),
      detail: `Zone idéale : ${config.sumMin} – ${config.sumMax}`,
    });
  }
  rules.push({
    key: "parity", label: "Parité (Pairs / Impairs)", value: `${evens}P / ${odds}I`,
    status: validateParity(nums, config) ? "pass" : "fail",
    detail: config.kenoMode ? "Écart max 2 entre pairs et impairs" : "Idéal 2/3 ou 3/2",
  });
  rules.push({
    key: "consecutive", label: "Suites consécutives", value: `${cp} paire(s)`,
    status: cp === 0 ? "pass" : cp === 1 ? "warn" : "fail",
    detail: "Maximum 1 paire consécutive tolérée",
  });
  rules.push({
    key: "decades", label: "Répartition par dizaines", value: `${dc} dizaine(s)`,
    status: dc >= 3 ? "pass" : dc === 2 ? "warn" : "fail",
    detail: "Couvrir au moins 3 dizaines différentes",
  });
  rules.push({
    key: "anti_share", label: "Anti-partage (numéros > 31)", value: `${high} numéro(s) > 31`,
    status: high >= 3 ? "pass" : high >= 1 ? "warn" : "fail",
    detail: "≥ 3 numéros > 31 pour éviter le partage de gains",
  });
  rules.push({
    key: "memory", label: `Mémoire du dernier tirage`, value: last ? `${common} commun(s)` : "n/a",
    status: common <= 1 ? "pass" : "fail",
    detail: "Max 1 numéro commun avec le tirage précédent",
  });

  const score = calculateConformityScore(nums, config, draws);
  return { rules, score };
}

// ---- Generation (rejection sampling / Monte-Carlo) ----
export function generateOptimizedGrid(config, draws, strategy, opts = {}) {
  const { locked = [], excluded = [], kenoPick } = opts;
  const pool = [];
  for (let n = 1; n <= config.max; n++) pool.push(n);
  const k = config.kenoMode ? (kenoPick || config.pick) : config.pick;

  // weights
  const weights = {};
  for (let n = 1; n <= config.max; n++) weights[n] = 1;
  if (strategy === "delay") {
    const gaps = computeGaps(config, draws);
    for (let n = 1; n <= config.max; n++) weights[n] = (gaps[n] || 0) + 1;
  }
  if (strategy === "anti_share") {
    for (let n = 1; n <= config.max; n++) weights[n] = n > 31 ? 3 : 1;
  }

  const maxAttempts = 10000;
  let best = null;
  let bestScore = -1;
  for (let a = 0; a < maxAttempts; a++) {
    const candidate = weightedSampleWithout(pool, weights, k, locked, excluded);
    if (candidate.length < k) break;
    const okSum = validateSum(candidate, config);
    const okParity = validateParity(candidate, config);
    const okCons = validateConsecutives(candidate);
    const okMem = validateMemory(candidate, draws && draws[0]);
    const okDec = validateDecades(candidate);
    const okAnti = strategy === "anti_share" ? validateAntiShare(candidate) : true;
    const score = calculateConformityScore(candidate, config, draws);
    if (okSum && okParity && okCons && okMem && okDec && okAnti) {
      return { grid: candidate, bonus: drawBonus(config, excluded), score, attempts: a + 1, fallback: false };
    }
    if (score > bestScore) {
      bestScore = score;
      best = candidate;
    }
  }
  return { grid: best || weightedSampleWithout(pool, weights, k, locked, excluded),
           bonus: drawBonus(config, excluded), score: Math.max(0, bestScore), attempts: maxAttempts, fallback: true };
}

function drawBonus(config, excluded = []) {
  if (!config.bonusPick) return [];
  const pool = [];
  for (let n = 1; n <= config.bonusMax; n++) pool.push(n);
  const w = {};
  pool.forEach((n) => (w[n] = 1));
  return weightedSampleWithout(pool, w, config.bonusPick, [], []);
}
